import React, {
  useRef,
  useState,
  useEffect,
  ChangeEvent,
  DragEvent,
} from "react";
import { FileText, Upload } from "lucide-react";
import { useTranslation } from "react-i18next";
import * as pdfjsLib from "pdfjs-dist";
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

import { useCV, StoredCV } from "../hooks/useCV";
import { useSubscription } from "../hooks/useSubscription";
// import { useProfile } from "../hooks/useProfile";
import type { FileInfo } from "../types";
import SelectCVModal from "./SelectCvModal";
import ProfileModal from "./ProfileModal";
import PackagesModal from "./PackagesModal";

interface UploadAreaProps {
  onFileContentChange: (
    content: string | null,
    info: FileInfo | null,
    file: File | null
  ) => void;
  fileInfo: FileInfo | null;
  onReset: () => void;
  showMessage: (message: string, type: "error" | "success") => void;
}

const UploadArea: React.FC<UploadAreaProps> = ({
  onFileContentChange,
  fileInfo,
  onReset,
  showMessage,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAreaRef = useRef<HTMLDivElement>(null);
  const { cvs } = useCV();
  const { subscription } = useSubscription();
  const { t } = useTranslation();

  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isPackagesModalOpen, setIsPackagesModalOpen] = useState(false);

  useEffect(() => {
    if (isSelectModalOpen || isProfileModalOpen || isPackagesModalOpen) {
      document.body.style.overflow = "hidden"; // disable background scroll
    } else {
      document.body.style.overflow = "auto"; // re-enable
    }

    return () => {
      document.body.style.overflow = "auto"; // cleanup
    };
  }, [isSelectModalOpen, isProfileModalOpen, isPackagesModalOpen]);

  useEffect(() => {
    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = () => {
      setIsDragging(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFile(e.dataTransfer.files[0]);
      }
    };

    const element = uploadAreaRef.current;
    if (element) {
      element.addEventListener(
        "dragover",
        handleDragOver as unknown as EventListener
      );
      element.addEventListener(
        "dragleave",
        handleDragLeave as unknown as EventListener
      );
      element.addEventListener("drop", handleDrop as unknown as EventListener);
    }

    return () => {
      if (element) {
        element.removeEventListener(
          "dragover",
          handleDragOver as unknown as EventListener
        );
        element.removeEventListener(
          "dragleave",
          handleDragLeave as unknown as EventListener
        );
        element.removeEventListener(
          "drop",
          handleDrop as unknown as EventListener
        );
      }
    };
  }, []);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file: File) => {
    if (!file) {
      return;
    }

    try {
      // Validation de la taille
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        showMessage(
          t("error.fileTooLarge", {
            maxSize: (maxSize / 1024 / 1024).toFixed(2),
          }),
          "error"
        );
        return;
      }

      // Validation du format
      const fileName = file.name.toLowerCase();
      const validExtensions = [".pdf", ".doc", ".docx", ".txt"];
      const isValidExtension = validExtensions.some(ext =>
        fileName.endsWith(ext)
      );

      if (!isValidExtension) {
        showMessage(
          t("error.unsupportedFormat", { formats: validExtensions.join(", ") }),
          "error"
        );
        return;
      }

      const fileInfo: FileInfo = {
        name: file.name,
        size: file.size,
        type: file.type || getFileTypeFromName(file.name),
        lastModified: file.lastModified,
      };
      onFileContentChange(null, fileInfo, file);
    } catch (error) {
      showMessage(
        t("error.processingError", {
          error: error instanceof Error ? error.message : "Unknown error",
        }),
        "error"
      );
    }
  };

  const getFileTypeFromName = (fileName: string): string => {
    const lowerFileName = fileName.toLowerCase();
    if (lowerFileName.endsWith(".pdf")) return "application/pdf";
    if (lowerFileName.endsWith(".doc")) return "application/msword";
    if (lowerFileName.endsWith(".docx"))
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    if (lowerFileName.endsWith(".txt")) return "text/plain";
    return "application/octet-stream";
  };

  const getFileTypeDescription = (type: string, name: string): string => {
    const lowerName = name.toLowerCase();

    if (type.includes("pdf") || lowerName.endsWith(".pdf"))
      return t("upload.file_types.pdf");
    if (type.includes("msword") || lowerName.endsWith(".doc"))
      return t("upload.file_types.word_doc");
    if (type.includes("wordprocessingml") || lowerName.endsWith(".docx"))
      return t("upload.file_types.word_docx");
    if (type.includes("text") || lowerName.endsWith(".txt"))
      return t("upload.file_types.text");
    return t("upload.file_types.document");
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Use profile CV - updated logic
  const handleUseProfileCV = () => {
    const isFreeTier = !subscription || subscription.plan === "free"; 

    if (!cvs || cvs.length === 0) {
      // Open ProfileModal to allow user to add CVs
      setIsProfileModalOpen(true);
      return;
    }

    if (isFreeTier || cvs.length === 1) {
      // For free users or users with only one CV, select it directly
      handleCvSelectFromModal(cvs[0]);
    } else {
      // For subscribed users with multiple CVs, open the selection modal
      setIsSelectModalOpen(true);
    }
  };

  // New handler for when a CV is selected from the modal
  const handleCvSelectFromModal = (selectedCv: StoredCV) => {
    setIsSelectModalOpen(false);
    onReset();
    const fileInfo: FileInfo = {
      name: selectedCv.filename,
      size: selectedCv.file_size,
      type: selectedCv.file_type,
      lastModified: new Date(selectedCv.updated_at).getTime(),
    };
    onFileContentChange(selectedCv.content, fileInfo, null);
    showMessage(
      t("upload.profile_cv_loaded", { filename: selectedCv.filename }),
      "success"
    );
  };

  if (fileInfo) {
    return (
      <div className="w-full border-3 border-dashed border-gray-300 rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 text-center mb-4 sm:mb-6 md:mb-8 bg-gray-50">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 lg:gap-6">
          <div className="w-full mb-4 lg:mb-0">
            <h3 className="text-base sm:text-lg md:text-xl font-semibold text-green-600 mb-2 sm:mb-3 md:mb-4">
              ✅ {t("upload.file_loaded_success")}
            </h3>
            <div className="space-y-1 sm:space-y-2 text-left text-xs sm:text-sm md:text-base">
              <p className="break-words">
                <strong>📁 {t("upload.file_name")}:</strong> {fileInfo.name}
              </p>
              <p>
                <strong>📊 {t("upload.file_size")}:</strong>{" "}
                {(fileInfo.size / 1024).toFixed(2)} KB
              </p>
              <p>
                <strong>📄 {t("upload.file_type")}:</strong>{" "}
                {getFileTypeDescription(fileInfo.type, fileInfo.name)}
              </p>
              <p>
                <strong>⏰ {t("upload.loaded_at")}:</strong>{" "}
                {new Date().toLocaleString()}
              </p>
            </div>
          </div>
          <button
            onClick={onReset}
            className="bg-red-500 hover:bg-red-600 text-white py-2 sm:py-3 px-3 sm:px-4 md:px-5 rounded-lg font-semibold transition-colors w-full lg:w-[fit-content] text-sm sm:text-base lg:whitespace-nowrap text-center"
          >
            🗑️ {t("upload.delete")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`w-full border-3 border-dashed ${
          isDragging
            ? "border-indigo-500 bg-indigo-50"
            : "border-gray-300 bg-gray-50"
        } 
                rounded-2xl p-4 sm:p-6 md:p-8 lg:p-12 text-center mb-4 sm:mb-6 md:mb-8 transition-all cursor-pointer
                hover:border-indigo-400 hover:bg-gray-100 hover:shadow-md`}
        onClick={handleUploadClick}
        ref={uploadAreaRef}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept=".pdf,.doc,.docx,.txt,.html,.xml"
          onChange={handleFileChange}
        />

        <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-2 sm:mb-3 md:mb-4 lg:mb-5">
          📄
        </div>
        <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-semibold mb-1 sm:mb-2 md:mb-3">
          {t("upload.title")}
        </h2>
        <p className="text-gray-600 mb-2 sm:mb-3 md:mb-4 text-xs sm:text-sm md:text-base lg:text-lg">
          {t("upload.subtitle")}
        </p>
        <p className="text-gray-500 text-xs sm:text-sm md:text-base mb-3 sm:mb-4 md:mb-5 lg:mb-6">
          {t("upload.supported_formats")} ({t("upload.max_size")})
        </p>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 justify-center items-center w-full max-w-2xl mx-auto">
          <button
            onClick={e => {
              e.stopPropagation();
              handleUseProfileCV();
            }}
            className={`${
              cvs && cvs.length > 0
                ? "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                : "bg-gradient-to-r from-gray-400 to-gray-500"
            } text-white py-2 sm:py-3 px-3 sm:px-4 md:px-6 rounded-full font-semibold hover:shadow-lg transition-all transform hover:-translate-y-1 flex items-center w-full sm:w-auto justify-center text-xs sm:text-sm md:text-base`}
            // disabled={!cvs || cvs.length === 0}
          >
            {cvs && cvs.length > 0 ? (
              <>
                <FileText className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 mr-1 sm:mr-2" />
                {t("upload.analyze_from_profile")}
              </>
            ) : (
              <>
                <FileText className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 mr-1 sm:mr-2" />
                {t("upload.add_to_profile")}
              </>
            )}
          </button>

          <button
            onClick={e => {
              e.stopPropagation();
              handleUploadClick();
            }}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2 sm:py-3 px-3 sm:px-4 md:px-6 rounded-full 
                    font-semibold hover:shadow-lg transition-all transform hover:-translate-y-1 flex items-center w-full sm:w-auto justify-center text-xs sm:text-sm md:text-base"
          >
            <Upload className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 mr-1 sm:mr-2" />
            {t("upload.browse_files")}
          </button>
        </div>

        <div className="mt-2 sm:mt-3 md:mt-4 p-2 sm:p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-200 max-w-2xl mx-auto">
          {cvs && cvs.length > 0 ? (
            <p className="text-xs sm:text-sm md:text-base text-blue-800">
              💡 <strong>{t("upload.quick_analysis")}:</strong>{" "}
              {t("upload.use_profile_cv_advice")}
            </p>
          ) : (
            <p className="text-xs sm:text-sm md:text-base text-blue-800">
              💡 <strong>{t("upload.tip")}:</strong>{" "}
              {t("upload.add_profile_cv_advice")}
            </p>
          )}
        </div>
      </div>

      <SelectCVModal
        isOpen={isSelectModalOpen}
        onClose={() => setIsSelectModalOpen(false)}
        onSelect={handleCvSelectFromModal}
        cvs={cvs}
      />

      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        handlePackagesModal={() => {
          // Close profile modal and open packages modal
          setIsProfileModalOpen(false);
          setIsPackagesModalOpen(true);
        }}
      />

      <PackagesModal
        isOpen={isPackagesModalOpen}
        onClose={() => setIsPackagesModalOpen(false)}
        onUpgrade={() => {
          // Handle upgrade logic if needed
          setIsPackagesModalOpen(false);
        }}
        currentPlan={subscription?.plan || 'free'}
      />
    </>
  );
};

export default UploadArea;
